import { setExperiencePlace } from 'actions/ExperienceActions'
import Flag from 'LuxKit/Flag'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineLocationArrowIcon from 'LuxKit/Icons/line/LineLocationArrowIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import SolidSearchIcon from 'LuxKit/Icons/solid/SolidSearchIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import Places, { getPlaceByRegionCode } from 'constants/places'
import GeoContext from 'contexts/geoContext'
import switchFunc from 'lib/function/switchFunc'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import ExperiencesPageLocationModalItem from './ExperiencesPageLocationModalItem'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'

const DestinationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    column-gap: ${rem(20)};
    row-gap: ${rem(8)};
  }
`

const SearchSection = styled(VerticalSpacer)`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: flex;
    gap: ${rem(24)};
  }
`

interface PlaceItem {
  place: App.Place
  nameOverride?: string
  icon?: React.ReactNode
}

const getAustralianCities = switchFunc<Array<PlaceItem>>({
  luxuryescapes: [
    { place: Places.Australia, nameOverride: 'All Australia', icon: <Flag countryCode="au" /> },
    { place: Places.Sydney, icon: <LineMapMarkerIcon /> },
    { place: Places.Brisbane, icon: <LineMapMarkerIcon /> },
    { place: Places.Melbourne, icon: <LineMapMarkerIcon /> },
    { place: Places.Perth, icon: <LineMapMarkerIcon /> },
    { place: Places.Adelaide, icon: <LineMapMarkerIcon /> },
  ],
}, [
  { place: Places.Australia, nameOverride: 'All Australia', icon: <Flag countryCode="au" /> },
  { place: Places.Sydney, icon: <LineMapMarkerIcon /> },
  { place: Places.Melbourne, icon: <LineMapMarkerIcon /> },
  { place: Places.GoldCoast, icon: <LineMapMarkerIcon /> },
  { place: Places.Perth, icon: <LineMapMarkerIcon /> },
  { place: Places.Brisbane, icon: <LineMapMarkerIcon /> },
  { place: Places.Hobart, icon: <LineMapMarkerIcon /> },
  { place: Places.Adelaide, icon: <LineMapMarkerIcon /> },
])

const NewZealandCities: Array<PlaceItem> = [
  { place: Places.NewZealand, nameOverride: 'All New Zealand', icon: <Flag countryCode="nz" /> },
  { place: Places.Auckland, icon: <LineMapMarkerIcon /> },
  { place: Places.Wellington, icon: <LineMapMarkerIcon /> },
  { place: Places.Christchurch, icon: <LineMapMarkerIcon /> },
  // { place: Places.Hamilton, icon: <LineMapMarkerIcon /> },
  { place: Places.Queenstown, icon: <LineMapMarkerIcon /> },
]

const regionBlacklist = new Set([
  'hk',
  'cn',
  'de',
  'kr',
  'il',
  'mo',
  'qa',
  'ph',
  'sa',
  'za',
  'tw',
])

export type ExperiencesPageLocationModalResponse = {
  type: App.ExperiencePlaceType
  place: App.ExperiencePlace
}

interface Props {
  experiencePlace: App.ExperiencePlace
  onSearchByDestination?: React.MouseEventHandler<HTMLButtonElement>
}

interface ExperiencesPageLocationModalResult {
  type: App.ExperiencePlaceType
  place: {
    placeId: string | undefined
    userSelected: boolean
  }
}

function ExperiencesPageLocationModal(props: Props) {
  const {
    experiencePlace,
    onSearchByDestination,
  } = props
  const intl = useIntl()
  const localizedNameOverrides: Record<string, string> = useMemo(() => ({
    'All Australia': intl.formatMessage({
      id: 'YGgTtBLDdMrUfuVeATKL',
      defaultMessage: 'All Australia',
      description: 'Location filter option to show all experiences in Australia',
    }),
    'All New Zealand': intl.formatMessage({
      id: 'dqqMYKnex3v08UjAv7ta',
      defaultMessage: 'All New Zealand',
      description: 'Location filter option to show all experiences in New Zealand',
    }),
  }), [intl])
  const trackEvent = useTrackExperienceOfferEvent()
  const modalElement = useModalElementContext<ExperiencesPageLocationModalResult | undefined>()
  const dispatch = useAppDispatch()
  const geo = useContext(GeoContext)
  const [localPlace, setLocalPlace] = useState<App.ExperiencePlace>(experiencePlace)

  useEffect(() => {
    setLocalPlace(experiencePlace)
  }, [experiencePlace])

  const searchByDestination = useCallback<MouseEventHandler<HTMLButtonElement>>((e) => {
    onSearchByDestination?.(e)
    modalElement?.resolve(undefined)
  }, [modalElement, onSearchByDestination])

  const applySelection = useCallback((type: App.ExperiencePlaceType, placeId: string | undefined, userSelected: boolean) => {
    dispatch(setExperiencePlace(type, {
      placeId,
      userSelected,
    }))

    if (type === 'currentLocation') {
      trackEvent('GEOLOCATION_USE_CURRENT_LOCATION_CLICKED')
    } else {
      trackEvent('GEOLOCATION_SELECTED')
    }

    modalElement?.resolve({
      type: localPlace.type,
      place: {
        placeId: localPlace.placeId,
        userSelected: localPlace.userSelected,
      },
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent, dispatch are a stable function
  }, [localPlace, localPlace, localPlace])

  const onSelectRegion = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const type = 'region'
    const placeId = getPlaceByRegionCode(e.currentTarget.dataset.code).id
    const userSelected = true
    setLocalPlace({
      type,
      placeId,
      userSelected,
    })
    applySelection(type, placeId, userSelected)
  }, [applySelection])

  const onSelectPlace = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const type = 'place'
    const placeId = e.currentTarget.dataset.placeid
    const userSelected = true
    setLocalPlace({
      type,
      placeId,
      userSelected,
    })
    applySelection(type, placeId, userSelected)
  }, [applySelection])

  const onCurrentLocationClick = useCallback(() => {
    const type = 'currentLocation'
    const userSelected = true
    setLocalPlace({
      type,
      userSelected,
    })
    applySelection(type, undefined, userSelected)
  }, [applySelection])

  const localCities = useMemo(() => {
    if (geo.currentRegionCode === 'AU') {
      return getAustralianCities(config.BRAND)
    } else if (geo.currentRegionCode === 'NZ') {
      return NewZealandCities
    }
  }, [geo.currentRegionCode])

  const regionPlaces = useMemo(() => {
    if (config.BRAND === 'luxuryescapes') {
      return config.regions.filter(r => !regionBlacklist.has(r.code.toLowerCase()))
    }
    return []
  }, [])

  return <ModalBase
    size="L"
    onAfterOpen={() => trackEvent('GEOLOCATION_OPENED')}
    onClose={() => trackEvent('GEOLOCATION_CLOSED')}
  >
    <ModalHeader
      title={intl.formatMessage({
        id: 'kOUIZ_mXmbQkiNEH1sng',
        defaultMessage: 'Explore offers around the world',
        description: 'Title for the location selection modal on experiences page',
      })}
      subtitle={intl.formatMessage({
        id: 'E3F0g_Kq0J7DmIl7HXpP',
        defaultMessage: 'We’ll show you the best offers around that area',
        description: 'Subtitle for the location selection modal on experiences page',
      })}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={20}>
          <DestinationGrid>
            <ExperiencesPageLocationModalItem
              onClick={onCurrentLocationClick}
              icon={<LineLocationArrowIcon />}
              selected={localPlace.type === 'currentLocation' && localPlace.userSelected}
            >
              {intl.formatMessage({
                id: '5IEwLcunZga5f_NiBFuw',
                defaultMessage: 'Use my current location',
                description: 'Button text to use geolocation for experience search',
              })}
            </ExperiencesPageLocationModalItem>
          </DestinationGrid>
          {localCities && <VerticalSpacer gap={8}>
            <BodyText variant="medium" weight="bold">{geo.currentRegionName}</BodyText>
            <DestinationGrid>
              {localCities.map(placeItem => <ExperiencesPageLocationModalItem
                key={placeItem.place.id}
                icon={placeItem.icon}
                data-placeid={placeItem.place.id}
                onClick={onSelectPlace}
                selected={
                  localPlace.type === 'place' &&
                  localPlace.placeId === placeItem.place.id
                }
              >
                {(placeItem.nameOverride && localizedNameOverrides[placeItem.nameOverride]) ?? placeItem.place.name}
              </ExperiencesPageLocationModalItem>)}
            </DestinationGrid>
          </VerticalSpacer>}
          {regionPlaces.length > 0 && <VerticalSpacer gap={8}>
            <BodyText variant="medium" weight="bold">{intl.formatMessage({
              id: 'F9E9LnI6SuovLDbHws-B',
              defaultMessage: 'Other regions',
              description: 'Section heading for other region options in location modal',
            })}
            </BodyText>
            <DestinationGrid>
              <ExperiencesPageLocationModalItem
                icon={<LineGlobeIcon />}
                data-placeid={Places.Anywhere.id}
                onClick={onSelectPlace}
                selected={localPlace.placeId === Places.Anywhere.id}
              >
                {intl.formatMessage({
                  id: 'UNAdtsaPB0NBjczowY-J',
                  defaultMessage: 'Worldwide',
                  description: 'Label for worldwide location option in location modal',
                })}
              </ExperiencesPageLocationModalItem>
              {regionPlaces.map(region => <ExperiencesPageLocationModalItem
                key={region.code}
                icon={<Flag countryCode={region.code} />}
                data-code={region.code}
                onClick={onSelectRegion}
                selected={
                  localPlace.type === 'region' &&
                  getPlaceByRegionCode(region.code).id === localPlace.placeId
                }
              >
                {region.name}
              </ExperiencesPageLocationModalItem>)}
            </DestinationGrid>
          </VerticalSpacer>}
          {onSearchByDestination && (
            <SearchSection gap={16}>
              <div>
                <Heading variant="heading6">{intl.formatMessage({
                  id: 'hzEuyI5JZVtZYr-aIZfZ',
                  defaultMessage: 'Have a place in mind?',
                  description: 'Heading prompting user to search by destination in location modal',
                })}
                </Heading>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'aspas6OtHY7rQERSNY6D',
                    defaultMessage: 'Search for exclusive offers around the world in any city, country or region.',
                    description: 'Description text encouraging destination search in location modal',
                  })}
                </BodyText>
              </div>
              <TextButton
                fit="mobile-full-width"
                onClick={searchByDestination}
                kind="primary"
                startIcon={<SolidSearchIcon />}
              >
                {intl.formatMessage({
                  id: 'ZxjRRNNSIOWS0cWIaUI9',
                  defaultMessage: 'Search by destination',
                  description: 'Button text to search experiences by destination in location modal',
                })}
              </TextButton>
            </SearchSection>
          )}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

function mapStateToProps(state: App.State) {
  return {
    experiencePlace: state.experience.experiencePlace,
  }
}

export default connect(mapStateToProps)(ExperiencesPageLocationModal)
